import { Option } from '@sapphire/result';

import { BaseMapper, Undefinable } from '@ps/utils';

import {
  HEALTH_CHECK_STATUS,
  HealthCheckKpi,
  MultiCountry,
  PROJECT_TYPE,
  ProjectType,
  SURVEY_STATUS,
  SurveyChildScore,
} from '@ps/survey-kernel';
import {
  CPI,
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  GroupedChildSurveyRef,
  GroupedStats,
  ProjectStats,
  Survey,
  SurveyFeasibilityMapper,
  SurveyPace,
  SurveyStatusCount,
  SurveySummary,
  TotalCost,
  TrackerStats,
} from '@ps/survey-utils';

import { HealthCheckKpiDto } from '../dtos/health-check.dto';
import {
  GroupedChildSurveyMetaDto,
  ParentGroupedStatsDto,
  TcChannelDto,
  TrackerStatsDto,
} from '../dtos/projects.dto';
import { SurveyCountResponseDto, SurveyDto } from '../dtos/survey.dto';
import { GetSurveyByIdDto } from '../dtos/get-survey-by-id.dto';
import { SurveyLookupResponseDto } from '../dtos/survey-lookup.dto';
import { SurveyQualificationMapper } from './survey-qualification.mapper';
import { SurveyQuotaMapper } from './survey-quota-mapper';
import { SurveyFeaturesMapper } from './survey-features/survey-features.mapper';

export class SurveyMapper extends BaseMapper<SurveyDto, Survey> {
  private readonly _qualificationMapper = new SurveyQualificationMapper();
  private readonly _quotaMapper = new SurveyQuotaMapper();
  private readonly _surveyFeasibilityMapper = new SurveyFeasibilityMapper();
  private readonly _surveyFeaturesMapper = new SurveyFeaturesMapper();

  public fromDto(dto: SurveyDto): Survey {
    const mc = this._mapMc(dto);
    const project = this._mapProject({
      project: dto.project,
      project_type: dto.project_type,
    });
    const surveyGrouping = dto.survey_grouping?.length
      ? dto.survey_grouping
      : undefined;
    const aida = this._mapAida(dto);
    const qbp = this._mapQbp(dto);
    const advancedFeasibilityFilters =
      this._surveyFeasibilityMapper.mapToAdvancedFeasibilityFilters(dto);

    return {
      id: dto.ps_survey_id,
      aida,
      qbp,
      advancedFeasibilityFilters,
      status: dto.ps_survey_status,
      endDate: dto.end_date,
      inProgress: dto.in_progress,
      completesRequired: dto.completes_required,
      fielded: dto.fielded,
      pace: this._mapPace(dto),
      avgCpi: this._averageCpi(dto),
      aCpi: dto.acpi,
      currentIr: dto.current_incidence,
      countryName: dto.locale.country_name,
      countryCode: dto.locale.country_code,
      languageShortCode: dto.locale.language_translate,
      languageName: dto.locale.language_name,
      languageCode: dto.locale.language_code,
      buyerCompanyId: dto.buyer_company,
      earningsPerClick: dto.epc,
      title: dto.survey_title,
      companyName: dto.company_name,
      billingId: dto.billing_id,
      isCloneSurvey: !!dto.is_clone_survey,
      isCloneBillingIdChanged: !!dto.is_clone_billing_id_changed,
      currencySymbol: dto.currency_symbol,
      mc,
      project,
      surveyGrouping,
      projectType: dto.project_type,
      mcUniqueId: dto.mc_unique_id,
      projectId: dto.ps_project_id,
      isParent: this._isParent(dto.project_type, mc, project, dto.tc_count),
      isQbpChild: dto.is_qbp_child_survey,
      isQbpEnabled: dto.is_qbp_enable,
      isAidaEnabled: dto.is_aida_enabled,
      isFeotEnabled: dto.is_feot_enabled,
      launchDate: dto.survey_launch_date,
      expectedLoi: dto.expected_loi,
      expectedIr: dto.expected_ir,
      fieldTime: dto.field_time,
      clickBalancing: dto.click_balancing,
      defaultPurePriceOverride: dto.default_pure_price_override,
      isPurePriceRateCardEnable: dto.is_pure_price_rate_card_enable,
      isPriceRecoEnabled: dto.price_reco,
      supplierDefaultAllocation: dto.supplier_default_allocation,
      buyerPm: dto.buyer_pm,
      reconCompletes: dto.rec_complt_count,
      manualFixCompletes: dto.manual_fix_complt_count,
      currentCost: dto.current_cost,
      reconciliation: dto.reconciliation,
      templateId: dto.template_id,
      tagIds: dto.tagIds ?? [],
      tcCount: dto.tc_count,
      surveyScore: dto.survey_score,
      crmId: dto.crm_id,
      operatorPm: dto.operator_pm,
      marginBank: dto.margin_bank,
      actualMargin: dto.actual_margin,
      healthCheck: this._mapHealthCheck(dto.healthCheck),
      childScores: this._mapProjectChildScores(dto, mc, project),
      totalCost: this._mapTotalCost(dto),
      cpi: this._mapCpi(dto),
      isMarginOverrideEnabled: !!dto.is_enable_margin_override,
      isMarginMaxEnabled: !!dto.is_enable_margin_max,
      isManualScpiOverrideEnabled: !!dto.manual_scpi_override_enabled,
      marginOverride: dto.margin_override?.margin,
      offerPrice: dto.offer_price,
      createdAt: dto.creation_date,
      modifiedAt: dto.mod_on,
      qualifications: this._qualificationMapper.fromDtoList(
        dto.qualifications || [],
      ),
      quotas: this._quotaMapper.fromDtoList(dto.quotas || []),
      channelType: '',
      isPending: false,
      tags: dto.tags,
      blendId: dto.blend_id ?? '',
      subscriberEmails: dto.subscriber_emails,
      asanaId: dto.asana_id,
      liveUrl: dto.live_url,
      testUrl: dto.test_url,
      testPsSurveyEntryLink: dto.test_ps_survey_entry_link,
      fieldDateInfo: this._mapFieldDateInfo(dto),
      surveyOpenCloseDateInfo: this._mapSurveyOpenCloseDateInfo(dto),
      srLaunchTs: dto.sr_launch_ts,
      srCloseTs: dto.sr_close_ts,
      openDates: dto.open_dates,
      buyerName: dto.buyer_name,
      buyerEmail: dto.buyer_email,
      samplesName: dto.samples_name,
      inclExcl: dto.incl_excl,
      hasPsidRefList: dto.hasPSIDRefList,
      supplierSurveyGrouping: this._mapSupplierSurveyGrouping(dto),
      redirectUrls: this._mapRedirectUrls(dto),
      surveyExternalId: dto.survey_external_id,
      surveyClient: dto.survey_client,
      buyerMessage: dto.buyer_message,
      offerId: dto.offer_id,
      termId: dto.term_id,
      qlc: this._mapQlc(dto),
      schedulePause: this._surveyFeaturesMapper.mapSchedulePause(dto),
      supplierCustomRedirectField: dto.supplier_custom_redirect_field,
      urlTransform: dto.url_transform,
      uniqueLinks: this._surveyFeaturesMapper.mapUniqueLinks(dto),
      pmEmails: dto.pm_emails,
      basicToken: this._surveyFeaturesMapper.mapBasicToken(dto),
      isModularSurvey: dto.is_modular_survey,
      supplierAllocationThrottle: dto.supplier_allocation_throttle,
      bypassLoiThreshold: dto.bypass_loi_threshold,
      scheduleLaunch: this._surveyFeaturesMapper.mapScheduleLaunch(dto),
      autoLaunch: dto.auto_launch,
      softLaunch: this._surveyFeaturesMapper.mapSoftLaunch(dto),
      quotaThrottling: dto.quota_throttling,
      feot: this._surveyFeaturesMapper.mapFeot(dto),
      dqSettings: this._surveyFeaturesMapper.mapDqSettings(dto),
      isDqEnabled: dto.is_dq_enable,
      exclusionPeriod: dto.exclusion_period,
      isPermanentExclusion: dto.is_permanent_exclusion,
    };
  }

  public fromCountResponseDto(
    response: SurveyCountResponseDto,
  ): SurveyStatusCount {
    return Object.fromEntries(
      response.total.map((item) => [item._id, item.count]),
    );
  }

  public fromDetailsDto(dto: GetSurveyByIdDto): Survey {
    return {
      ...this.fromDto(dto),
      aCpi: dto.acpi ?? dto.cpi?.current,
      currentCost: dto.total_cost?.current,
      buyerPm: dto.buyer_pm ?? dto.bpm?.nm,
      operatorPm: dto.operator_pm ?? dto.pm?.nm,
      buyerProjectManager: this._surveyFeaturesMapper.mapProjectManager(
        dto.bpm,
      ),
      operatorProjectManager: this._surveyFeaturesMapper.mapProjectManager(
        dto.pm,
      ),
      ...this._mapLoiVariants(dto),
      ...this._mapIrVariants(dto),
    };
  }

  public fromLookupDto(dto: SurveyLookupResponseDto): SurveySummary {
    return {
      id: dto.id,
      title: dto.tle ?? '',
      billingId: dto.billing_id,
      statusText: dto.st_text,
    };
  }

  private _mapLoiVariants(
    dto: GetSurveyByIdDto,
  ): Pick<Survey, 'recentLoi' | 'overallLoi' | 'launchLoi'> {
    return {
      recentLoi: dto.loi?.recent,
      overallLoi: dto.loi?.overall,
      launchLoi: dto.loi?.launch,
    };
  }

  private _mapIrVariants(
    dto: GetSurveyByIdDto,
  ): Pick<Survey, 'recentIr' | 'overallIr' | 'launchIr'> {
    return {
      recentIr: dto.incidence?.recent,
      overallIr: dto.incidence?.overall,
      launchIr: dto.incidence?.launch,
    };
  }

  private _mapPace(dto: SurveyDto): SurveyPace {
    return {
      expected: dto.pacing_data?.cumulative_pace.expected ?? 0,
      actual: dto.fielded ?? 0,
    };
  }

  private _mapTotalCost(dto: SurveyDto): TotalCost {
    return {
      current: dto.total_cost?.current,
      projected: dto.total_cost?.projected,
      launch: dto.total_cost?.launch,
    };
  }

  private _mapCpi(dto: SurveyDto): CPI {
    return {
      current: dto.cpi?.current,
      launch: dto.cpi?.launch,
      projected: dto.cpi?.projected,
      feasibilityCpi: dto.cpi?.feasibility_cpi,
      priceOverride: dto.price_override,
    };
  }

  private _averageCpi(dto: SurveyDto): number {
    if (dto.ps_survey_status === SURVEY_STATUS.DRAFT) {
      return 0;
    }

    if (dto.fielded > 0) {
      return dto.average_cpi || 0;
    }

    return dto.acpi || 0;
  }

  private _mapMc(dto: SurveyDto): MultiCountry | undefined {
    return Option.from(dto.mc?.parent).match({
      some: (parent) => ({
        svp: !!dto.mc.SVP,
        parent: !!parent,
        surveys: (dto.mc.surveys ?? []).map((s) => ({
          surveyId: s.surveyId,
          countryCode: s.countryCode ?? '',
          languageShortCode: s.languageCode ?? '',
          healthCheck: this._mapHealthCheck(s.healthCheck),
          score: s.score,
          scorePercentage: s.scorePercentage,
        })),
      }),
      none: () => undefined,
    });
  }

  private _mapHealthCheck(
    kpis: HealthCheckKpiDto[] | undefined,
  ): HealthCheckKpi[] {
    if (!kpis?.length) {
      return [];
    }
    const validStatusTexts: readonly string[] =
      Object.keys(HEALTH_CHECK_STATUS);
    return kpis
      .filter(
        (kpi) =>
          typeof kpi.status === 'number' &&
          typeof kpi.name === 'string' &&
          validStatusTexts.includes(kpi.status_text),
      )
      .map((kpi) => ({
        status: kpi.status,
        statusText: kpi.status_text,
        name: kpi.name,
      }));
  }

  private _mapProject(
    dto: Pick<SurveyDto, 'project' | 'project_type'>,
  ): ProjectStats {
    if (!dto.project || !dto.project_type) {
      return undefined;
    }
    if (dto.project_type === PROJECT_TYPE.TRACKER) {
      return this._mapTrackerStats(dto.project as TrackerStatsDto);
    }
    if (
      dto.project_type === PROJECT_TYPE.GROUPED &&
      (dto.project as ParentGroupedStatsDto).parent
    ) {
      return this._mapGroupedStats(dto.project as ParentGroupedStatsDto);
    }
    return undefined;
  }

  private _mapTrackerStats(dto: TrackerStatsDto): TrackerStats {
    if (dto.parent) {
      return {
        parent: true,
        waveCount: dto.wave_count,
        exclusionRule: dto.exclusion_rule,
        frequency: dto.frequency,
        surveys: (dto.surveys ?? []).map((s) => ({
          surveyId: s.surveyId,
          waveId: s.wave_id,
          waveExclusions: s.wave_exclusions,
          status: s.status,
          score: s.score,
          scorePercentage: s.scorePercentage,
        })),
        counts: {
          totalDraft: dto.counts?.total_draft ?? 0,
          totalLaunched: dto.counts?.total_launched ?? 0,
        },
      };
    }
    return {
      parent: false,
      waveId: dto.wave_id,
      waveExclusions: dto.wave_exclusions,
    };
  }

  private _mapGroupedStats(dto: ParentGroupedStatsDto): GroupedStats {
    return {
      parent: true,
      surveys: (dto.surveys ?? []).map((s) => this._mapGroupedSurveyChild(s)),
      counts: {
        totalDraft: dto.counts?.total_draft ?? 0,
        totalLaunched: dto.counts?.total_launched ?? 0,
      },
    };
  }

  private _mapGroupedSurveyChild(
    dto: GroupedChildSurveyMetaDto,
  ): GroupedChildSurveyRef {
    const [languageShortCode, countryCode] = dto.survey_localization.split('_');
    return {
      surveyId: dto.surveyId,
      status: dto.status,
      title: dto.title,
      countryCode: countryCode ?? '',
      languageShortCode: languageShortCode ?? '',
      score: dto.score,
      scorePercentage: dto.scorePercentage,
    };
  }

  private _mapProjectChildScores(
    survey: SurveyDto,
    mc: Undefinable<MultiCountry>,
    project: Undefinable<TrackerStats | GroupedStats>,
  ): Undefinable<SurveyChildScore[]> {
    if (survey.project_type === PROJECT_TYPE.MCQB && mc?.parent === true) {
      return mc.surveys.map((childSurvey) =>
        this._mapSingleChildScore(childSurvey),
      );
    }
    if (
      survey.project_type === PROJECT_TYPE.TRACKER &&
      project?.parent === true
    ) {
      const trackerParent = project as Extract<TrackerStats, { parent: true }>;
      return trackerParent.surveys.map((childSurvey) =>
        this._mapSingleChildScore(childSurvey, {
          countryCode: survey.locale.country_code,
          languageShortCode: survey.locale.language_translate,
        }),
      );
    }
    if (
      survey.project_type === PROJECT_TYPE.GROUPED &&
      project?.parent === true
    ) {
      const groupedParent = project as Extract<GroupedStats, { parent: true }>;
      return groupedParent.surveys.map((childSurvey: GroupedChildSurveyRef) =>
        this._mapSingleChildScore(childSurvey),
      );
    }
    return undefined;
  }

  private _mapSingleChildScore(
    survey: {
      surveyId: number;
      countryCode?: string;
      languageShortCode?: string;
      score?: number | null;
      scorePercentage?: number | null;
    },
    localeOverride?: Pick<
      SurveyChildScore,
      'countryCode' | 'languageShortCode'
    >,
  ): SurveyChildScore {
    return {
      surveyId: survey.surveyId,
      countryCode: localeOverride?.countryCode ?? survey.countryCode ?? '',
      languageShortCode:
        localeOverride?.languageShortCode ?? survey.languageShortCode ?? '',
      score: survey.score ?? 0,
      scorePercentage: survey.scorePercentage ?? 0,
    };
  }

  private _isParent(
    projectType: Undefinable<ProjectType>,
    mc: Undefinable<MultiCountry>,
    project: Undefinable<TrackerStats | GroupedStats>,
    tcCount?: number,
  ): boolean {
    if (projectType === PROJECT_TYPE.MCQB) return !!mc?.parent;
    if (
      projectType === PROJECT_TYPE.TRACKER ||
      projectType === PROJECT_TYPE.GROUPED
    )
      return !!project?.parent;
    return (tcCount ?? 0) > 0;
  }

  private _mapAida({ aida }: SurveyDto): Survey['aida'] {
    return {
      enabled: !!aida?.enabled,
      selectedQuotaIds: aida?.selected_quota_ids ?? [],
      category: aida?.category,
    };
  }

  private _mapQbp({ qbp }: SurveyDto): Survey['qbp'] {
    return {
      enable: !!qbp?.enable,
      category: qbp?.category,
      quotaIds: qbp?.quota_ids ?? [],
      selectedQuotaIds: qbp?.selectedQuotaIds ?? [],
    };
  }

  private _mapFieldDateInfo(dto: SurveyDto): Survey['fieldDateInfo'] {
    if (!dto.field_date_info) {
      return undefined;
    }
    return {
      creationDate: dto.field_date_info.creation_date,
      endDate: dto.field_date_info.end_date,
      daysRemaining: dto.field_date_info.days_remaining,
      daysInField: dto.field_date_info.days_in_field,
    };
  }

  private _mapSurveyOpenCloseDateInfo(
    dto: SurveyDto,
  ): Survey['surveyOpenCloseDateInfo'] {
    if (!dto.survey_open_close_date_info) {
      return undefined;
    }
    return {
      openDates: dto.survey_open_close_date_info.open_dates,
      surveyLaunch: dto.survey_open_close_date_info.survey_launch,
      surveyClose: dto.survey_open_close_date_info.survey_close,
    };
  }

  private _mapSupplierSurveyGrouping(
    dto: SurveyDto,
  ): Survey['supplierSurveyGrouping'] {
    if (!dto.supplier_survey_grouping) {
      return undefined;
    }
    return {
      exclusionPeriod: dto.supplier_survey_grouping.exclusion_period,
      surveyIds: dto.supplier_survey_grouping.survey_ids,
    };
  }

  private _mapRedirectUrls(dto: SurveyDto): Survey['redirectUrls'] {
    if (!dto.redirect_urls) {
      return undefined;
    }
    return {
      suppUrlCompletes: dto.redirect_urls.supp_url_completes,
      suppUrlTerms: dto.redirect_urls.supp_url_terms,
      suppUrlOverquota: dto.redirect_urls.supp_url_overquota,
    };
  }

  private _mapQlc(dto: SurveyDto): Survey['qlc'] {
    return {
      enable: !!dto.qlc?.enable,
      multipleQuotas: {
        enable: !!dto.qlc?.multiple_quotas?.enable,
        countByQuota: !!dto.qlc?.multiple_quotas?.count_by_quota,
        compensateByQuota: !!dto.qlc?.multiple_quotas?.compensate_by_quota,
      },
    };
  }
}

const PENDING_CHANNEL_TITLES: Record<string, string> = {
  REP: 'REPLICA',
  UNT: 'UNTARGETED',
  SPA: 'SPECIFIC AUDIENCE',
  TL: 'TARGETED LIST',
  LZ: 'Large Zip',
  AP: 'Allocation Priority',
  SR: 'Stealth Replica',
};

const DEFAULT_PENDING_CHANNEL_TITLE = 'CHANNEL';

export class TcChannelMapper extends BaseMapper<TcChannelDto, Survey> {
  public fromDto(dto: TcChannelDto): Survey {
    if (dto.create_status_description === 'PENDING') {
      return this._fromPendingDto(dto);
    }
    return this._fromReadyDto(dto);
  }

  private _fromPendingDto(dto: TcChannelDto): Survey {
    return {
      ...this._defaults(),
      title:
        PENDING_CHANNEL_TITLES[dto.channel_type] ??
        DEFAULT_PENDING_CHANNEL_TITLE,
      channelType: dto.channel_type,
      childStatusId: dto.child_status_id,
      isPending: true,
    };
  }

  private _fromReadyDto(dto: TcChannelDto): Survey {
    return {
      ...this._defaults(),
      id: dto.survey_id ?? 0,
      status: dto.status ?? SURVEY_STATUS.DRAFT,
      inProgress: dto.in_progress ?? 0,
      completesRequired: dto.goal ?? 0,
      fielded: dto.fielded ?? 0,
      earningsPerClick: dto.epc ?? 0,
      title: dto.title ?? '',
      currencySymbol: dto.symbol ?? '',
      avgCpi: dto.avg_cpi,
      currentIr: dto.current_ir,
      launchDate: dto.launch_date,
      currentCost: dto.cpi_current ?? 0,
      totalStarts: dto.total_starts ?? 0,
      last24Starts: dto.last_24_starts ?? 0,
      channelType: dto.channel_type,
      isPending: false,
    };
  }

  private _defaults(): Survey {
    return {
      id: 0,
      status: SURVEY_STATUS.DRAFT,
      inProgress: 0,
      completesRequired: 0,
      fielded: 0,
      pace: { expected: 0, actual: 0 },
      earningsPerClick: 0,
      title: '',
      currencySymbol: '',
      currentCost: 0,
      totalStarts: 0,
      last24Starts: 0,
      channelType: '',
      isPending: false,
      countryName: '',
      countryCode: '',
      languageShortCode: '',
      companyName: '',
      billingId: '',
      isCloneSurvey: false,
      isCloneBillingIdChanged: false,
      buyerCompanyId: 0,
      expectedLoi: 0,
      expectedIr: 0,
      fieldTime: 0,
      clickBalancing: 0,
      isPurePriceRateCardEnable: false,
      isPriceRecoEnabled: false,
      supplierDefaultAllocation: false,
      isParent: false,
      isQbpChild: false,
      isQbpEnabled: false,
      isAidaEnabled: false,
      isFeotEnabled: false,
      healthCheck: [],
      isMarginOverrideEnabled: false,
      isMarginMaxEnabled: false,
      isManualScpiOverrideEnabled: false,
      advancedFeasibilityFilters: EMPTY_ADVANCED_FEASIBILITY_FILTERS,
      blendId: '',
    };
  }
}
